import { useState, useRef } from "react"
import PropTypes from "prop-types"
import SelectionTypeConfirmationEmail from "./SelectionTypeConfirmationEmail"
import { Checkbox, Pane } from "components/ui"
import { StackView, Button } from "@planningcenter/tapestry-react"
import PersonalInfoOptionsAndRestrictions from "./PersonalInfoOptionsAndRestrictions"
import { camelToSnakeKeys, cleanCurrency, isBlank } from "utils/helpers"

const SelectionTypeForm = ({
  onSave,
  onCancel,
  onDelete,
  signup,
  selectionType,
  selectionType: { currencySymbol, canBeDeleted = false }
}) => {
  if (isBlank(selectionType)) {
    return null
  }
  const [validation, setValidation] = useState({})
  const [name, setName] = useState(selectionType.name)
  const [emailConfirmation, setEmailConfirmation] = useState(selectionType.confirmationEmailMessage)
  const [publiclyAvailable, setPubliclyAvailable] = useState(selectionType.publiclyAvailable)
  const [allowDeposit, setAllowDeposit] = useState(selectionType.allowDeposit)
  const [minimumDeposit, setMinimumDeposit] = useState(
    selectionType.allowDeposit && selectionType.minimumDepositCents
      ? selectionType.minimumDepositCents / 100
      : null
  )

  const [price, setPrice] = useState(
    selectionType.priceFormatted ? selectionType.priceFormatted : ""
  )

  const collectionOptionsAndRestrictions = useRef(null)

  const validateForm = () => {
    let validation = {}

    if (name === null || name.trim() === "") {
      validation.name = "Name can not be blank."
    }

    if (allowDeposit && Number(minimumDeposit) > Number(price)) {
      validation.minimumDeposit = "Minimum deposit can not be more than the price"
    }

    setValidation(validation)
    return validation
  }

  const handleAllowDepositChange = value => {
    if (!value) {
      setMinimumDeposit(null)
    } else {
      setMinimumDeposit(0)
    }
    setAllowDeposit(value)
  }

  const handleDelete = () => {
    return alertModal(
      {
        title: `Delete selection?`,
        text: "This can't be undone.",
        showCancelButton: true,
        cancelButtonText: "No, don't delete it",
        confirmButtonText: "Yes, delete it!",
        type: "warning",
        customClass: "destructive-warning",
        closeOnConfirm: true
      },
      () =>
        onDelete(selectionType).catch(error => {
          const text = error.errors.map(message => message.detail).join(", ")
          return alertModal({
            title: "Cannot delete",
            text,
            showCancelButton: false,
            type: "error",
            closeOnConfirm: true,
            confirmButtonText: "Ok"
          })
        })
    )
  }

  const handleCancel = () => {
    return onCancel()
  }

  const handleSave = () => {
    const validate = validateForm()

    if (!isBlank(validate)) {
      return
    }
    const restrictions =
      collectionOptionsAndRestrictions?.current?.getOptionsAndRestrictionState() ?? {}

    return onSave(
      {
        name,
        confirmation_email_message: emailConfirmation,
        publicly_available: publiclyAvailable,
        allow_deposit: allowDeposit,
        price: cleanCurrency(price),
        minimum_deposit: isNaN(Number(minimumDeposit)) ? 0 : Number(minimumDeposit),
        ...camelToSnakeKeys({
          ...restrictions,
          collectionOptions: {
            ...camelToSnakeKeys(restrictions.collectionOptions ?? {})
          }
        })
      },
      selectionType
    )
  }

  const handlePriceChange = value => {
    if (validateMoneyInput(value)) {
      setPrice(value.replace(/ /, ""))
    }
  }

  const handleMinimumDepositChange = value => {
    if (validateMoneyInput(value)) {
      setMinimumDeposit(value.replace(/ /, ""))
    }
  }

  const validateMoneyInput = value => {
    const valueToEvaluate = Number(value.replace(/\D/g, ""))
    return !isNaN(valueToEvaluate)
  }

  return (
    <section>
      <div className="d-f@lg">
        <div className="pr-2@lg f-2">
          <div className="f-2">
            <Pane marginBottom={4}>
              <Pane.Header distribution="start" spacing={0.5}>
                <Pane.Title>Selection</Pane.Title>
              </Pane.Header>
              <Pane.Body spacing={1}>
                <div className="d-f g-2">
                  <div className="f-2 mb-2">
                    <label htmlFor="name">Name</label>
                    {validation.name && (
                      <div className="c-error font-size-5">{validation.name}</div>
                    )}
                    <input
                      id="name"
                      autoComplete="off"
                      defaultValue={name}
                      maxLength={255}
                      name="attendee_type[name]"
                      placeholder="Selection name"
                      type="text"
                      className={`${validation.name && "error-input"}`}
                      onKeyUp={e => e.keyCode === 13 && handleSave()}
                      onChange={e => setName(e.target.value)}
                    />
                  </div>
                  {signup.hasPaymentsModule && (
                    <div className="f-1">
                      <label htmlFor="cost">Cost</label>

                      <div className="prepended-label">
                        <span className="label">{currencySymbol}</span>
                        <input
                          id="price"
                          autoComplete="off"
                          value={price}
                          name="attendee_type[price]"
                          placeholder="Price"
                          type="text"
                          onChange={e => handlePriceChange(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {signup.hasAttendeesModule && (
                  <PersonalInfoOptionsAndRestrictions
                    selectionType={selectionType}
                    signup={signup}
                    ref={collectionOptionsAndRestrictions}
                  />
                )}
              </Pane.Body>
            </Pane>

            <SelectionTypeConfirmationEmail
              onChange={setEmailConfirmation}
              value={emailConfirmation}
            />
          </div>
        </div>
        <div className="pl-2@lg f-1 mt-4 mt-0@sm">
          <Pane>
            <Pane.Header distribution="start" spacing={0.5}>
              <Pane.Title>Options</Pane.Title>
            </Pane.Header>
            <Pane.Body spacing={1}>
              <Checkbox
                name="publicly_available"
                htmlFor={`publiclyAvailable`}
                text="Show on Church Center"
                checked={publiclyAvailable}
                onSelect={() => setPubliclyAvailable(!publiclyAvailable)}
                wrapperClasses=""
              />
              {signup.hasPaymentsModule && (
                <>
                  <Checkbox
                    name="allow_deposit"
                    htmlFor={`allowDeposit`}
                    text="Allow partial payments"
                    checked={allowDeposit}
                    onSelect={() => handleAllowDepositChange(!allowDeposit)}
                    wrapperClasses=""
                  />
                  <div className="attendee-type-options-js">
                    <div
                      className={`mt-2 minimum-deposit-js ${allowDeposit ? "" : "js-visibility"}`}
                      id="minimum_deposit"
                    >
                      <label className="mb-1" htmlFor="minimum_deposit">
                        Minimum deposit
                      </label>
                      <div className="prepended-label">
                        {validation.minimumDeposit && (
                          <div className="c-error font-size-5">{validation.minimumDeposit}</div>
                        )}
                        <span className="label">{currencySymbol}</span>
                        <input
                          id="minimum_deposit"
                          autoComplete="off"
                          value={minimumDeposit}
                          name="attendee_type[minimum_deposit]"
                          placeholder="Minimum deposit"
                          type="text"
                          className={`${validation.minimumDeposit && "error-input"}`}
                          onChange={e => handleMinimumDepositChange(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </Pane.Body>
          </Pane>
          <StackView axis="horizontal" distribution="space-between" marginTop={2}>
            <StackView axis="horizontal">
              {selectionType && (
                <Button
                  theme="destroy"
                  disabled={!canBeDeleted}
                  onClick={handleDelete}
                  title="Delete"
                  data-tooltip={
                    !canBeDeleted
                      ? "This selection type cannot be deleted because there are already registered attendees"
                      : null
                  }
                />
              )}
            </StackView>
            <StackView axis="horizontal" spacing={1}>
              <Button variant="outline" title="Cancel" onClick={handleCancel} />
              <Button theme="create" title="Save" onClick={handleSave} />
            </StackView>
          </StackView>
        </div>
      </div>
    </section>
  )
}

SelectionTypeForm.propTypes = {
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  signup: PropTypes.shape({
    firstEventDate: PropTypes.string,
    hasPaymentsModule: PropTypes.bool,
    hasAttendeesModule: PropTypes.bool
  }),
  selectionType: PropTypes.shape({
    name: PropTypes.string,
    confirmationEmailMessage: PropTypes.string,
    currencySymbol: PropTypes.string,
    canBeDeleted: PropTypes.bool,
    priceCents: PropTypes.string,
    priceFormatted: PropTypes.string,
    minimumDepositCents: PropTypes.string,
    publiclyAvailable: PropTypes.bool,
    allowDeposit: PropTypes.bool
  })
}

export default SelectionTypeForm
